<template>
  <BaseCenteredModal
    v-model:is-modal-open="localModalVisible"
    :title="$t('invoicing.chift_send_attempts_history')"
    size="medium"
    @close="close"
  >
    <template #body>
      <div v-if="loaded" class="flex w-full flex-col gap-4">
        <template v-if="attempts.length">
          <div class="overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead class="bg-secondary text-contrast-secondary">
                <tr>
                  <th class="p-2">{{ $t('invoicing.chift_send_attempt_date') }}</th>
                  <th class="p-2">{{ $t('invoicing.chift_send_attempt_trigger') }}</th>
                  <th class="p-2">{{ $t('invoicing.chift_send_attempt_result') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="attempt in attempts"
                  :key="attempt.id"
                  class="border-b align-top"
                >
                  <td class="whitespace-nowrap p-2 text-xs">
                    {{ attemptDate(attempt) }}
                    <span class="block opacity-60">
                      {{ $t('invoicing.chift_send_attempt_number', { number: attempt.attempt }) }}
                    </span>
                  </td>
                  <td class="capitalize-first p-2 text-xs">
                    {{ triggerLabel(attempt.trigger) }}
                  </td>
                  <td class="p-2">
                    <div class="flex items-start gap-1.5">
                      <FontAwesomeIcon
                        :icon="[
                          'fas',
                          attempt.status === 'success' ? 'circle-check' : 'circle-xmark'
                        ]"
                        :class="
                          attempt.status === 'success'
                            ? 'mt-0.5 text-green-500'
                            : 'mt-0.5 text-red-500'
                        "
                      />
                      <div class="flex flex-1 flex-col gap-0.5">
                        <span
                          v-if="attempt.category"
                          class="capitalize-first text-xs font-medium opacity-70"
                        >
                          {{ categoryLabel(attempt.category) }}
                        </span>
                        <!-- The actionable wording, rendered backend-side at read
                             time so an improved translation applies to an attempt
                             recorded months ago. -->
                        <span v-if="attempt.message" class="capitalize-first">
                          {{ attempt.message }}
                        </span>
                        <!-- What Chift literally says, in English, then the raw
                             text from the accounting software. Both are what
                             support compares against the accounting side. -->
                        <span v-if="attempt.chift_description" class="text-xs italic opacity-70">
                          {{ attempt.chift_description }}
                        </span>
                        <span
                          v-if="showsDetail(attempt)"
                          class="text-xs break-all opacity-50"
                        >
                          {{ attempt.detail }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <PaginationComponent
            v-if="pagination.last_page > 1"
            :entries-length="pagination.total"
            :page-length="pagination.per_page"
            :current-page="pagination.current_page"
            @update:current-page="goToPage"
          />
        </template>

        <div v-else class="text-center text-sm opacity-70">
          {{ $t('invoicing.chift_send_attempts_empty') }}
        </div>
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import PaginationComponent from '@c/PaginationComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapActions } from 'vuex'

export default {
  name: 'ChiftSendAttemptsModal',
  components: { BaseCenteredModal, PaginationComponent, FontAwesomeIcon },
  props: {
    /**
     * The invoice whose trips to the accounting software are listed.
     */
    invoiceId: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      localModalVisible: false,
      attempts: [],
      // Mirrors the endpoint's own payload; paginating server-side keeps an
      // invoice retried hundreds of times from being serialised in one go.
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      },
      loaded: false
    }
  },
  watch: {
    invoiceId: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.localModalVisible = false

          return
        }

        this.fetchAttempts(1)
      }
    }
  },
  methods: {
    ...mapActions({
      processFetchInvoiceChiftSendAttemptsAction:
        'invoicing/fetchInvoiceChiftSendAttempts'
    }),
    /**
     * Categories come from ChiftErrorCategory, triggers from SendTrigger.
     * Falling back to the raw value keeps a case added backend-side readable
     * until its key is translated, rather than printing the key on screen.
     */
    categoryLabel(category) {
      const key = `invoicing.chift_send_category_${category}`
      const label = this.$t(key)

      return label === key ? category : label
    },
    triggerLabel(trigger) {
      const key = `invoicing.chift_send_trigger_${trigger}`
      const label = this.$t(key)

      return label === key ? trigger : label
    },
    attemptDate(attempt) {
      const occurredAt = attempt.finished_at ?? attempt.started_at

      if (!occurredAt) {
        return '—'
      }

      return this.$h.formatDate(
        occurredAt,
        'D MMM YYYY HH:mm',
        this.$i18n.locale.substring(0, 2)
      )
    },
    /**
     * `detail` holds the accounting software's raw text, which the rendered
     * message is often built from. Repeating it identically underneath adds
     * nothing, so it only shows when it says something else.
     */
    showsDetail(attempt) {
      return !!attempt.detail && attempt.detail !== attempt.message
    },
    goToPage(page) {
      if (page === this.pagination.current_page) {
        return
      }

      this.fetchAttempts(page)
    },
    fetchAttempts(page) {
      this.processFetchInvoiceChiftSendAttemptsAction({
        invoice_id: this.invoiceId,
        page,
        per_page: this.pagination.per_page
      })
        .then((response) => {
          this.attempts = response.attempts ?? []
          this.pagination = response.pagination ?? this.pagination
          this.loaded = true
          this.localModalVisible = true
        })
        .catch(() => {
          // Opening the history is an explicit click: failing silently left the
          // user staring at a modal that did nothing.
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          this.close()
        })
    },
    close() {
      this.localModalVisible = false
      this.attempts = []
      this.loaded = false
      this.$emit('close')
    }
  }
}
</script>
